import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';

import { OfferSource } from '@cds';
import { PlayerStats } from '@cds/statistics/player';
import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ValueSliderDirective } from '@frontend/sports/common/ui/value-slider';
import { isNumber } from 'lodash-es';

import { PlayerStatisticsStoreData } from '../player-statistics/player-statistics.model';
import { PlayerStatisticsService, SeasonStatisticsType } from '../player-statistics/player-statistics.service';

@Component({
    selector: 'ms-player-stats',
    styleUrl: 'player-stats.component.scss',
    templateUrl: './player-stats.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ValueSliderDirective, NgClass],
})
export class PlayerStatsComponent implements OnInit, OnChanges {
    private siteCore = inject(Sitecore);
    private changeDetectionRef = inject(ChangeDetectorRef);
    private epcotConfig = inject(EpcotConfig);

    @HostBinding('class') get className(): string {
        return this.isEpcotEnabled ? 'player-stats' : 'player-stats-container';
    }

    @Input() playerStats: PlayerStats[];
    @Input() playerStatsProps: string[];
    @Input() playerIds: number[];
    @Input() sportId: number;
    @Input() source?: OfferSource;
    @Input() isSeasonStatisticsEnabled: boolean = false;

    statistics = input<PlayerStatisticsStoreData>();
    marketId = input<string>();
    optionId = input<string>();
    seasonStatisticsMessage = input<string>();
    seasonStatisticsType = input<SeasonStatisticsType>();
    marketType = input<OptionGroupType>();

    playerStatisticsService = inject(PlayerStatisticsService);
    seasonStatistics = signal<string | undefined>(undefined);

    private siteCorePlayerStats: Record<string, string | undefined>;

    statsIndicator: string;
    isEpcotEnabled: boolean;
    private readonly INCREASED_CLASS = 'stat-increased';
    private readonly DECREASED_CLASS = 'stat-decreased';
    playerStatsData = {
        statsText: '',
        statsValue: 0,
    };

    constructor() {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;

        effect(() => {
            const store = this.statistics();
            if (!store?.isLoading && !store?.isError) {
                const statisticsData = this.playerStatisticsService.getPlayerStatistics(
                    store?.data,
                    this.marketId(),
                    this.optionId(),
                    false,
                    this.marketType(),
                );
                const statisticsType = this.seasonStatisticsType();

                let statisticsValue: string | undefined;
                if (statisticsType === SeasonStatisticsType.HitRate) {
                    statisticsValue = statisticsData?.hitRateValue;
                } else if (statisticsType === SeasonStatisticsType.Average) {
                    statisticsValue = statisticsData?.averageStatValue;
                }

                this.seasonStatistics.set(statisticsValue);
            }
        });
    }

    ngOnInit(): void {
        this.siteCorePlayerStats = this.siteCore.scoreboardPlayerStats[SportConstant[this.sportId]?.toString().toLowerCase()] || {};
        if (this.playerStats) {
            this.getPlayerStats(this.playerStats);
        }
    }

    ngOnChanges(changes: ISimpleChanges<PlayerStatsComponent>): void {
        if (changes.playerStats && !changes.playerStats.firstChange) {
            const currentStatValue = this.getStatsValue(changes.playerStats.currentValue);

            if (currentStatValue) {
                if (currentStatValue > this.playerStatsData.statsValue) {
                    this.statsIndicator = this.INCREASED_CLASS;
                } else if (currentStatValue < this.playerStatsData.statsValue) {
                    this.statsIndicator = this.DECREASED_CLASS;
                }
                this.playerStatsData.statsValue = currentStatValue;
                this.changeDetectionRef.markForCheck();
            }
        }

        if (changes.playerIds && !changes.playerIds.firstChange) {
            this.getPlayerStats(this.playerStats);
        }
    }

    private getPlayerStats(playerStats: PlayerStats[]): void {
        const selectedStatName = this.playerIds.length > 1 ? `Combined${this.playerStatsProps.join('')}` : this.playerStatsProps.join('');
        this.playerStatsData = {
            statsText: this.siteCorePlayerStats[selectedStatName] || '',
            statsValue: this.getStatsValue(playerStats),
        };
    }

    private getStatsValue(playerStats: PlayerStats[]): number {
        let statData = 0;
        const filteredStats = playerStats.find((x) => x.source === this.source);

        if (!filteredStats) {
            return statData;
        }

        this.playerIds.forEach((player) => {
            const playerStat = filteredStats.stats[player];
            if (playerStat) {
                const count = this.playerStatsProps.length;

                let statValue = this.playerStatsProps.reduce((result, stat) => result + (playerStat[stat.toLowerCase()] || 0), 0);

                // When multiple stats are present, calculate the average of the stats.
                if (count > 1 && isNumber(statValue)) {
                    statValue = Math.round(statValue / count);
                }
                statData += statValue;
            }
        });

        return statData;
    }
}
